import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { Table } from "primeng/table";

@Component({
  selector: "app-quotes",
  templateUrl: "./quotes.component.html",
  styleUrls: ["./quotes.component.css"],
})
export class QuotesComponent implements OnInit, OnChanges {
  @Input() nh3Quotes: any[] = [];
  @Input() cryoQuotes: any[] = [];
  @Output() quoteSelected = new EventEmitter<any>();
  @ViewChild("nh3Table") nh3Table?: Table;
  @ViewChild("cryoTable") cryoTable?: Table;

  currentTab: string = "NH3";

  nh3Rows: any[] = [];
  cryoRows: any[] = [];

  // prettier-ignore
  nh3Columns: any[] = [
    { field: "quoteName", header: "Quote Name", width: 220 },
    { field: "pickupLocation", header: "Pickup", width: 220 },
    { field: "dropoffLocation", header: "Dropoff", width: 220 },
    { field: "mileage", header: "Mileage", width: 110 },
    { field: "baseRate", header: "Base Rate", width: 130, type: "currency" },
    { field: "fscPerMile", header: "FSC/Mile", width: 130, type: "currency" },
    { field: "totalFsc", header: "Total FSC", width: 130, type: "currency" },
    { field: "allInRate", header: "All In Rate", width: 130, type: "currency" },
  ];

  // prettier-ignore
  cryoColumns: any[] = [
    { field: "quoteName", header: "Quote Name", width: 220 },
    { field: "pickupLocation", header: "Pickup", width: 220 },
    { field: "dropoffLocation", header: "Dropoff", width: 220 },
    { field: "mileage", header: "Mileage", width: 110 },
    { field: "linehaulTotal", header: "Linehaul", width: 130, type: "currency" },
    { field: "loadingTotal", header: "Loading", width: 130, type: "currency" },
    { field: "unloadingTotal", header: "Unloading", width: 130, type: "currency" },
    { field: "fscTotal", header: "FSC", width: 130, type: "currency" },
    { field: "insuranceTotal", header: "Insurance", width: 130, type: "currency" },
    { field: "total", header: "Invoice Total", width: 150, type: "currency" },
  ];

  ngOnInit(): void {
    this.syncRows();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["nh3Quotes"] || changes["cryoQuotes"]) {
      this.syncRows();
    }
  }

  setTab(tabName: string) {
    this.currentTab = tabName;
  }

  onQuoteDoubleClick(row: any) {
    this.quoteSelected.emit({
      quote: row?.__rawQuote || row,
      commodityType: row?.__commodityType || this.currentTab,
    });
  }

  formatCell(row: any, col: any) {
    const value = row[col.field];
    if (col.type === "currency") return value ?? 0;
    return value ?? "";
  }

  exportCurrentGridToExcel() {
    const isNh3 = this.currentTab === "NH3";
    const columns = isNh3 ? this.nh3Columns : this.cryoColumns;
    const rows = isNh3 ? this.nh3Rows : this.cryoRows;
    const table = isNh3 ? this.nh3Table : this.cryoTable;

    const visibleRows = table?.filteredValue ?? rows;
    const colNames = columns.map((col) => col.header);

    const plainRecords = visibleRows.map((row) =>
      columns.map((col) => this.escapeCsvValue(this.formatCell(row, col)))
    );

    plainRecords.unshift(colNames);

    let csvString = "";
    for (const rowItem of plainRecords) {
      csvString += rowItem.join(",") + "\r\n";
    }

    csvString = "data:application/csv," + encodeURIComponent(csvString);
    const link = document.createElement("A");
    link.setAttribute("href", csvString);
    link.setAttribute(
      "download",
      `${isNh3 ? "nh3_lpg" : "cryo"}_quotes_${new Date().toLocaleDateString()}.csv`
    );
    document.body.appendChild(link);
    link.click();
  }

  private syncRows() {
    this.nh3Rows = (this.nh3Quotes || []).map((quote) => this.mapNh3Quote(quote));
    this.cryoRows = (this.cryoQuotes || []).map((quote) => this.mapCryoQuote(quote));
  }

  private mapNh3Quote(quote: any) {
    return {
      quoteName: this.pick(quote, ["quoteName", "name", "rateName"]),
      pickupLocation: this.normalizeLocation(
        this.pick(quote, ["pickupLocationName", "pickupLocation", "pickup", "origin", "loadLocationName"])
      ),
      dropoffLocation: this.normalizeLocation(
        this.pick(quote, [
          "dropoffLocationName",
          "dropoffLocation",
          "deliveryLocation",
          "destination",
          "unloadLocationName",
        ])
      ),
      mileage: this.pick(quote, ["mileage", "miles", "routeMiles"]) || 0,
      baseRate: this.pick(quote, ["baseRate"]) || 0,
      fscPerMile: this.pick(quote, ["fscPerMile"]) || 0,
      totalFsc: this.pick(quote, ["totalFSC", "totalFsc", "fscAmount"]) || 0,
      allInRate: this.pick(quote, ["allInRate", "total"]) || 0,
      __rawQuote: quote,
      __commodityType: "NH3/LPG",
    };
  }

  private mapCryoQuote(quote: any) {
    return {
      quoteName: this.pick(quote, ["quoteName", "name", "rateName"]),
      pickupLocation: this.normalizeLocation(
        this.pick(quote, ["pickupLocationName", "pickupLocation", "pickup", "origin", "loadLocationName"])
      ),
      dropoffLocation: this.normalizeLocation(
        this.pick(quote, ["dropoffLocationName", "dropoffLocation", "dropoff", "destination", "unloadLocationName"])
      ),
      mileage: this.pick(quote, ["linehaulMiles", "mileage", "miles", "routeMiles"]) || 0,
      linehaulTotal: this.pick(quote, ["linehaulTotal", "linehaulAmount"]) || 0,
      loadingTotal: this.pick(quote, ["loadingTotal", "loadingAmount"]) || 0,
      unloadingTotal: this.pick(quote, ["unloadingTotal", "unloadingAmount"]) || 0,
      fscTotal: this.pick(quote, ["fuelSurchargeTotal", "fscTotal", "fscAmount", "totalFsc"]) || 0,
      insuranceTotal: this.pick(quote, ["insuranceTotal", "insuranceAmount"]) || 0,
      total: this.pick(quote, ["total", "allInRate"]) || 0,
      __rawQuote: quote,
      __commodityType: "Cryo",
    };
  }

  private pick(source: any, keys: string[]) {
    for (const key of keys) {
      if (source && source[key] !== undefined && source[key] !== null) return source[key];
    }
    return null;
  }

  private normalizeLocation(location: any) {
    if (location === undefined || location === null) return "";
    if (typeof location === "string") return location;
    if (typeof location === "object") {
      return location.name || location.locationId || location.id || "";
    }
    return String(location);
  }

  private escapeCsvValue(value: any): string {
    if (value === undefined || value === null) return "";
    const stringValue = String(value).replace(/\r?\n|\r/g, " ");
    if (stringValue.includes(",") || stringValue.includes("\"") || stringValue.includes("\n")) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }
}
